import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Location } from 'types/location.type';
import LrCheckbox from 'components/LrCheckbox';
import { formatDollar } from 'helpers/formatters';
import { UwBRLocationReview } from 'types/underwriting.type';
import { SubmissionDetails } from 'types/submission.type';
import {
    calculateBrPricingTable,
    getBrAnnualizedPremiumSubtotal,
    getBrLimitSubtotal,
    getBrPremiumSubtotal,
    getBrLocationTriaPremium,
} from 'helpers/brCalculationHelper';
import { calculateAnnualizedPremiumFromTotalPremium } from 'helpers/calculations';
import { TextInput } from 'components/Input';
import { numberMask } from 'helpers/masks';
import { BorderWrapper } from '../Styled/BrLocationPremiumReviewTable';

interface Props {
    location: Location;
    locationBrReview: UwBRLocationReview;
    submissionDetails: SubmissionDetails;
    onTriaChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    triaValue: number;
    readOnly: boolean;
}

const BrLocationReviewPremiumTable: React.FC<Props> = (props) => {
    const { location, locationBrReview, submissionDetails, onTriaChange, readOnly } = props;
    const pricingTable = calculateBrPricingTable(location, locationBrReview, submissionDetails);

    return (
        <TableContainer>
            <Table sx={{ overflowX: 'auto' }} aria-label="Premium Table" size="medium">
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell sx={{ fontWeight: 'bold' }}>Covered</TableCell>
                        <TableCell>Limit</TableCell>
                        <TableCell>Technical Rate</TableCell>
                        <TableCell>Adjusted Rate</TableCell>
                        <TableCell>Annualized Premium</TableCell>
                        <TableCell>Total Premium</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow key="hardCosts">
                        <TableCell>Hard Costs</TableCell>
                        <TableCell>
                            <LrCheckbox disabled checked={!!location.hardCosts && location.hardCosts > 0} />
                        </TableCell>
                        <TableCell>{formatDollar(location.hardCosts || 0, 0)}</TableCell>
                        <TableCell>{formatDollar(locationBrReview?.technicalFinalRate || 0)}</TableCell>
                        <TableCell>{formatDollar(locationBrReview?.finalRate || 0)}</TableCell>
                        <TableCell>
                            {formatDollar(
                                calculateAnnualizedPremiumFromTotalPremium(
                                    locationBrReview?.hardCostPremium || 0,
                                    submissionDetails?.effectiveDate,
                                    submissionDetails?.expirationDate
                                ),
                                0
                            )}
                        </TableCell>
                        <TableCell>{formatDollar(locationBrReview?.hardCostPremium || 0, 0)}</TableCell>
                    </TableRow>
                    <TableRow key="softCosts">
                        <TableCell>Soft Costs</TableCell>
                        <TableCell>
                            <LrCheckbox disabled checked={!!location.softCosts && location.softCosts > 0} />
                        </TableCell>
                        <TableCell>{formatDollar(location.softCosts || 0, 0)}</TableCell>
                        <TableCell>{formatDollar(pricingTable.technicalSoftCostRate || 0)}</TableCell>
                        <TableCell>{formatDollar(locationBrReview?.softCostFinalRate || 0)}</TableCell>
                        <TableCell>
                            {formatDollar(
                                calculateAnnualizedPremiumFromTotalPremium(
                                    locationBrReview?.softCostPremium || 0,
                                    submissionDetails?.effectiveDate,
                                    submissionDetails?.expirationDate
                                ),
                                0
                            )}
                        </TableCell>
                        <TableCell>{formatDollar(locationBrReview?.softCostPremium || 0, 0)}</TableCell>
                    </TableRow>
                    <TableRow key="lossOfRents">
                        <TableCell>Loss of Rents</TableCell>
                        <TableCell>
                            <LrCheckbox
                                disabled
                                checked={!!location.lossOfRentalIncome && location.lossOfRentalIncome > 0}
                            />
                        </TableCell>
                        <TableCell>{formatDollar(location.lossOfRentalIncome || 0, 0)}</TableCell>
                        <TableCell>{formatDollar(pricingTable.technicalLossOfRentalIncomeRate || 0)}</TableCell>
                        <TableCell>{formatDollar(locationBrReview?.lossOfRentalIncomeFinalRate || 0)}</TableCell>
                        <TableCell>
                            {formatDollar(
                                calculateAnnualizedPremiumFromTotalPremium(
                                    locationBrReview?.lossOfRentalIncomePremium || 0,
                                    submissionDetails?.effectiveDate,
                                    submissionDetails?.expirationDate
                                ),
                                0
                            )}
                        </TableCell>
                        <TableCell>{formatDollar(locationBrReview?.lossOfRentalIncomePremium || 0, 0)}</TableCell>
                    </TableRow>
                    <TableRow key="lossOfIncome">
                        <TableCell>Loss of Income</TableCell>
                        <TableCell>
                            <LrCheckbox
                                disabled
                                checked={!!location.lossOfGrossEarnings && location.lossOfGrossEarnings > 0}
                            />
                        </TableCell>
                        <TableCell>{formatDollar(location.lossOfGrossEarnings || 0, 0)}</TableCell>
                        <TableCell>{formatDollar(pricingTable.technicalLossOfGrossEarningsRate || 0)}</TableCell>
                        <TableCell>{formatDollar(locationBrReview?.lossOfGrossEarningsFinalRate || 0)}</TableCell>
                        <TableCell>
                            {formatDollar(
                                calculateAnnualizedPremiumFromTotalPremium(
                                    locationBrReview?.lossOfGrossEarningsPremium || 0,
                                    submissionDetails?.effectiveDate,
                                    submissionDetails?.expirationDate
                                ),
                                0
                            )}
                        </TableCell>
                        <TableCell>{formatDollar(locationBrReview?.lossOfGrossEarningsPremium || 0, 0)}</TableCell>
                    </TableRow>
                    <TableRow key="subTotal">
                        <TableCell>
                            <BorderWrapper>Program Premium</BorderWrapper>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <BorderWrapper>{formatDollar(getBrLimitSubtotal(location), 0)}</BorderWrapper>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <BorderWrapper>
                                {formatDollar(getBrAnnualizedPremiumSubtotal(locationBrReview, submissionDetails), 0)}
                            </BorderWrapper>
                        </TableCell>
                        <TableCell>
                            <BorderWrapper>{formatDollar(getBrPremiumSubtotal(locationBrReview), 0)}</BorderWrapper>
                        </TableCell>
                    </TableRow>
                    <TableRow key="tria">
                        <TableCell>Program TRIA</TableCell>
                        <TableCell>
                            <LrCheckbox disabled checked={locationBrReview?.hasTria} />
                        </TableCell>
                        <TableCell>{formatDollar(getBrLimitSubtotal(location), 0)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <TextInput
                                name="triaPercentage"
                                value={locationBrReview?.triaPercentage}
                                onChange={onTriaChange}
                                variant="underlined"
                                mask={numberMask}
                                endAdornment="%"
                                style={{ width: '3rem' }}
                                readOnly={readOnly}
                            />
                        </TableCell>
                        <TableCell>
                            {formatDollar(
                                calculateAnnualizedPremiumFromTotalPremium(
                                    getBrLocationTriaPremium(locationBrReview),
                                    submissionDetails?.effectiveDate,
                                    submissionDetails?.expirationDate
                                ),
                                0
                            )}
                        </TableCell>
                        <TableCell>{formatDollar(getBrLocationTriaPremium(locationBrReview), 0)}</TableCell>
                    </TableRow>
                    <TableRow key="total">
                        <TableCell>
                            <BorderWrapper>Total Program Premium</BorderWrapper>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <BorderWrapper>{formatDollar(getBrLimitSubtotal(location), 0)}</BorderWrapper>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <BorderWrapper>
                                {formatDollar(
                                    getBrAnnualizedPremiumSubtotal(locationBrReview, submissionDetails) +
                                        calculateAnnualizedPremiumFromTotalPremium(
                                            getBrLocationTriaPremium(locationBrReview),
                                            submissionDetails?.effectiveDate,
                                            submissionDetails?.expirationDate
                                        ),
                                    0
                                )}
                            </BorderWrapper>
                        </TableCell>
                        <TableCell>
                            <BorderWrapper>
                                {formatDollar(
                                    getBrPremiumSubtotal(locationBrReview) + getBrLocationTriaPremium(locationBrReview),
                                    0
                                )}
                            </BorderWrapper>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BrLocationReviewPremiumTable;
